import { useCallback } from 'react';
import { syncApiService } from '../services/syncApi';
import { tickerApi } from '../services/tickerApi';
import { userTickerApi } from '../services/userTickerApi';
import { trackTickerEvent } from '../utils/analytics';
import { useAuth } from '../contexts/AuthContext';

interface UseAddTickerParams {
  isAuthenticated: boolean;
  tickerList: string[];
  setTickerList: React.Dispatch<React.SetStateAction<string[]>>;
  setSyncingTickers: React.Dispatch<React.SetStateAction<Set<string>>>;
  tempTickerList?: string[];
  setTempTickerList?: React.Dispatch<React.SetStateAction<string[]>>;
  isEditingTickers?: boolean;
  
  // Callbacks for page-specific logic
  initializeEmptyData?: (ticker: string) => void;
  onTickerNameUpdate?: (ticker: string, tickerName: string, tickerSymbol?: string) => void;
  onSyncComplete: (ticker: string, tickerStatus: any) => Promise<void>;
  autoSaveList?: (tickers: string[]) => Promise<void>;
  saveListImmediately?: (tickers: string[]) => void | Promise<void>; // Called immediately after adding ticker, before sync API
  onError?: (ticker: string, error: Error) => void;
  
  // Configuration
  normalizeTicker?: (ticker: string) => string; // Default: uppercase
  pollingInterval?: number; // Default: 1000ms
  maxPollAttempts?: number; // Default: 30
  checkAlreadyExists?: boolean; // Default: false - if true, checks if ticker already exists and skips polling
  setIsAddingNewTicker?: (value: boolean) => void; // Optional flag for pages that need it
}

interface UseAddTickerReturn {
  handleAddTicker: (ticker: string) => Promise<void>;
}

/**
 * Hook for handling adding a ticker to the list across List* pages.
 * Handles common logic: normalization, sync API call, ticker name updates, polling, and data fetching.
 */
export const useAddTicker = ({
  isAuthenticated,
  tickerList,
  setTickerList,
  setSyncingTickers,
  tempTickerList,
  setTempTickerList,
  isEditingTickers = false,
  initializeEmptyData,
  onTickerNameUpdate,
  onSyncComplete,
  autoSaveList,
  saveListImmediately,
  onError,
  normalizeTicker = (ticker: string) => ticker.toUpperCase(),
  pollingInterval = 1000,
  maxPollAttempts = 30,
  checkAlreadyExists = false,
  setIsAddingNewTicker,
}: UseAddTickerParams): UseAddTickerReturn => {
  const { user } = useAuth();

  const handleAddTicker = useCallback(async (ticker: string) => {
    const normalizedTicker = normalizeTicker(ticker);
    
    // Check if ticker already exists
    if (tickerList.includes(normalizedTicker)) {
      return;
    }

    // Set flag if provided (for pages that need it)
    if (setIsAddingNewTicker) {
      setIsAddingNewTicker(true);
    }

    // Track ticker add event
    trackTickerEvent('add', normalizedTicker);

    // Show new column and syncing state immediately (no wait for APIs)
    const newList = [normalizedTicker, ...tickerList];
    setTickerList(newList);

    if (isEditingTickers && tempTickerList && setTempTickerList && !tempTickerList.includes(normalizedTicker)) {
      setTempTickerList([normalizedTicker, ...tempTickerList]);
    }

    setSyncingTickers(prev => new Set(prev).add(normalizedTicker));

    if (initializeEmptyData) {
      initializeEmptyData(normalizedTicker);
    }

    // Authenticated: ensure ticker_basics then persist list; on failure remove the column
    if (isAuthenticated) {
      try {
        console.log(`useAddTicker: Ensuring ticker_basics for ${normalizedTicker}...`);
        const basicsResponse = await syncApiService.syncOnlyTickerBasics(normalizedTicker);
        if (!basicsResponse.success) {
          console.error(`useAddTicker: syncOnlyTickerBasics failed for ${normalizedTicker}`);
          setTickerList(tickerList);
          if (isEditingTickers && tempTickerList && setTempTickerList) {
            setTempTickerList(tempTickerList);
          }
          setSyncingTickers(prev => { const next = new Set(prev); next.delete(normalizedTicker); return next; });
          if (setIsAddingNewTicker) setIsAddingNewTicker(false);
          if (onError) onError(normalizedTicker, new Error(basicsResponse.message || 'Ticker could not be added'));
          return;
        }
      } catch (err) {
        console.error('useAddTicker: syncOnlyTickerBasics error:', err);
        setTickerList(tickerList);
        if (isEditingTickers && tempTickerList && setTempTickerList) {
          setTempTickerList(tempTickerList);
        }
        setSyncingTickers(prev => { const next = new Set(prev); next.delete(normalizedTicker); return next; });
        if (setIsAddingNewTicker) setIsAddingNewTicker(false);
        if (onError) onError(normalizedTicker, err instanceof Error ? err : new Error(String(err)));
        return;
      }

      if (saveListImmediately) {
        try {
          await saveListImmediately(newList);
          console.log(`useAddTicker: List saved immediately for ${normalizedTicker}`);
        } catch (error) {
          console.error('useAddTicker: Error saving list immediately:', error);
          setTickerList(tickerList);
          if (isEditingTickers && tempTickerList && setTempTickerList) setTempTickerList(tempTickerList);
          setSyncingTickers(prev => { const next = new Set(prev); next.delete(normalizedTicker); return next; });
          if (setIsAddingNewTicker) setIsAddingNewTicker(false);
          if (onError) onError(normalizedTicker, error instanceof Error ? error : new Error(String(error)));
          return;
        }
      }
    } else if (saveListImmediately) {
      try {
        await saveListImmediately(newList);
        console.log(`useAddTicker: List saved immediately for ${normalizedTicker}`);
      } catch (error) {
        console.error('useAddTicker: Error saving list immediately:', error);
        // Unauthenticated: continue with sync even if save fails
      }
    }

    // Call sync API and wait for it to complete (Pro subscribers: also sync pros/cons on data-processor)
    try {
      const syncProsAndCons = Boolean(isAuthenticated && user?.hasProSubscription);
      console.log(
        `useAddTicker: Calling sync API for ${normalizedTicker}${syncProsAndCons ? ' (syncProsAndCons)' : ''}...`
      );
      const syncApiResponse = await syncApiService.syncTicker(normalizedTicker, {
        syncProsAndCons,
      });
      console.log(`useAddTicker: Sync API completed for ${normalizedTicker}`);
      
      // Check if ticker already existed (status: 'already_exists')
      const alreadyExists = checkAlreadyExists && (syncApiResponse as any)?.data?.status === 'already_exists';
      
      // Now that sync API has returned, call check-sync-status (mappings API) to get ticker name and sync status
      const api = isAuthenticated ? userTickerApi : tickerApi;
      const syncResponse = await api.checkTickerSyncStatus({
        ticker_symbols: [normalizedTicker],
      });
      
      const tickerStatus = syncResponse.data?.tickers?.find((t: any) => t.ticker_symbol === normalizedTicker);
      
      // Update ticker name mapping from sync response (after sync API returns)
      if (tickerStatus?.ticker_name && onTickerNameUpdate) {
        onTickerNameUpdate(normalizedTicker, tickerStatus.ticker_name, tickerStatus.ticker_symbol);
      }

      // Authenticated: list was already saved in step 2 (saveListImmediately). No second autoSaveList here.

      // If ticker already exists or is already synced, fetch data immediately without polling
      if (alreadyExists || tickerStatus?.is_synced) {
        console.log(`useAddTicker: ${normalizedTicker} already exists/is synced, fetching data immediately...`);
        
        // Remove from syncing set
        setSyncingTickers(prev => {
          const newSet = new Set(prev);
          newSet.delete(normalizedTicker);
          return newSet;
        });
        
        // Clear the flag if provided
        if (setIsAddingNewTicker) {
          setIsAddingNewTicker(false);
        }
        
        // Fetch data immediately since ticker is already synced
        await onSyncComplete(normalizedTicker, tickerStatus);
        console.log(`useAddTicker: Completed data fetch for ${normalizedTicker}, removed from syncing`);
        return;
      }
      
      console.log(`useAddTicker: ${normalizedTicker} is new, beginning polling...`);
      
      // Use a more controlled polling approach for new tickers
      let pollAttempts = 0;
      
      const pollForNewTicker = async () => {
        pollAttempts++;
        console.log(`useAddTicker: Polling attempt ${pollAttempts}/${maxPollAttempts} for ${normalizedTicker}`);
        
        try {
          const api = isAuthenticated ? userTickerApi : tickerApi;
          const syncResponse = await api.checkTickerSyncStatus({
            ticker_symbols: [normalizedTicker],
          });
          
          const tickerStatus = syncResponse.data?.tickers?.find((t: any) => t.ticker_symbol === normalizedTicker);
          console.log(`useAddTicker: Polling for ${normalizedTicker}, sync status:`, tickerStatus);
          
          if (tickerStatus?.is_synced) {
            console.log(`useAddTicker: ${normalizedTicker} is synced, fetching data...`);
            
            // Remove from syncing set
            setSyncingTickers(prev => {
              const newSet = new Set(prev);
              newSet.delete(normalizedTicker);
              return newSet;
            });
            
            // Clear the flag if provided
            if (setIsAddingNewTicker) {
              setIsAddingNewTicker(false);
            }
            
            // Fetch data specifically for the newly synced ticker
            await onSyncComplete(normalizedTicker, tickerStatus);
            console.log(`useAddTicker: Completed data fetch for ${normalizedTicker}, removed from syncing`);
            return; // Stop polling
          }
          
          // Check if we've reached max attempts
          if (pollAttempts >= maxPollAttempts) {
            console.warn(`useAddTicker: Max polling attempts reached for ${normalizedTicker}, stopping`);
            setSyncingTickers(prev => {
              const newSet = new Set(prev);
              newSet.delete(normalizedTicker);
              return newSet;
            });
            // Clear the flag even on failure
            if (setIsAddingNewTicker) {
              setIsAddingNewTicker(false);
            }
            return;
          }
          
          // Continue polling if not synced yet and within limits
          setTimeout(pollForNewTicker, pollingInterval);
        } catch (error) {
          console.error('useAddTicker: Error polling for new ticker sync:', error);
          // Remove from syncing set on error
          setSyncingTickers(prev => {
            const newSet = new Set(prev);
            newSet.delete(normalizedTicker);
            return newSet;
          });
          // Clear the flag on error
          if (setIsAddingNewTicker) {
            setIsAddingNewTicker(false);
          }
          // Call error handler if provided
          if (onError) {
            onError(normalizedTicker, error instanceof Error ? error : new Error(String(error)));
          }
        }
      };
      
      // Start polling after a short delay
      setTimeout(pollForNewTicker, pollingInterval);
    } catch (err) {
      console.error('useAddTicker: Failed to start sync for new ticker:', err);
      // Remove from syncing set on error
      setSyncingTickers(prev => {
        const newSet = new Set(prev);
        newSet.delete(normalizedTicker);
        return newSet;
      });
      // Clear the flag on error
      if (setIsAddingNewTicker) {
        setIsAddingNewTicker(false);
      }
      // Call error handler if provided
      if (onError) {
        onError(normalizedTicker, err instanceof Error ? err : new Error(String(err)));
      }
    }
  }, [
    normalizeTicker,
    tickerList,
    setTickerList,
    setSyncingTickers,
    tempTickerList,
    setTempTickerList,
    isEditingTickers,
    initializeEmptyData,
    onTickerNameUpdate,
    onSyncComplete,
    autoSaveList,
    saveListImmediately,
    onError,
    pollingInterval,
    maxPollAttempts,
    checkAlreadyExists,
    setIsAddingNewTicker,
    isAuthenticated,
    user?.hasProSubscription,
  ]);

  return {
    handleAddTicker,
  };
};

